import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calculateTimeRemaining(targetDate: string): TimeRemaining {
  const now = Date.now();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

interface HackathonCountdownProps {
  startsAt: string | null;
  hackingEndsAt: string | null;
  eventName: string;
  titleFont: string | null;
  // The two editions render the digits at different sizes and faces.
  digitClass: string;
}

export function HackathonCountdown({
  startsAt,
  hackingEndsAt,
  eventName,
  titleFont,
  digitClass,
}: HackathonCountdownProps) {
  const [now, setNow] = useState<number>(() => Date.now());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const isLive = Boolean(
    startsAt &&
      hackingEndsAt &&
      now >= new Date(startsAt).getTime() &&
      now < new Date(hackingEndsAt).getTime(),
  );

  const target = isLive ? hackingEndsAt : startsAt;
  if (!target) return null;

  const timeRemaining = calculateTimeRemaining(target);

  const cells: { label: string; value: number }[] = isLive
    ? [
        {
          label: 'Horas',
          value: timeRemaining.days * 24 + timeRemaining.hours,
        },
        { label: 'Minutos', value: timeRemaining.minutes },
        { label: 'Segundos', value: timeRemaining.seconds },
      ]
    : [
        { label: 'Días', value: timeRemaining.days },
        { label: 'Horas', value: timeRemaining.hours },
        { label: 'Minutos', value: timeRemaining.minutes },
        { label: 'Segundos', value: timeRemaining.seconds },
      ];

  const heading = isLive ? 'Tiempo de Hackeo' : 'Cuenta Regresiva';
  const subtitle = isLive
    ? `Tiempo restante de hackeo en ${eventName}`
    : `Tiempo restante para ${eventName}`;

  const gridCols = isLive ? 'grid-cols-3' : 'grid-cols-4';

  return (
    <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm">
      <h2 className={cn('mb-4 font-bold text-2xl text-primary', titleFont)}>
        {heading}
      </h2>
      <p className="mb-4 font-mono text-primary/70 text-sm">{subtitle}</p>
      <div className={`grid ${gridCols} gap-4`}>
        {cells.map((cell) => (
          <div key={cell.label} className="text-center">
            <div className="border-2 border-primary bg-background p-4">
              <div className={cn('text-primary', digitClass)}>
                {mounted ? String(cell.value).padStart(2, '0') : '00'}
              </div>
            </div>
            <div className="mt-2 font-mono text-primary/70 text-xs uppercase">
              {cell.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
